import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getReactionSummary, toggleReaction } from '@/lib/api/reactions';
import type { ReactionKind, TargetType } from '@/lib/api/reactions';
import { useAuth } from '@/lib/auth/useAuth';

const REACTION_META: { kind: ReactionKind; emoji: string; label: string }[] = [
  { kind: 'agree', emoji: '👍', label: 'Agree' },
  { kind: 'question', emoji: '❓', label: 'Question' },
  { kind: 'important', emoji: '⭐', label: 'Important' },
];

interface Props {
  targetType: TargetType;
  targetId: string;
}

export function ReactionBar({ targetType, targetId }: Props) {
  const { status } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = ['reactions', targetType, targetId];

  const { data: reactions = [] } = useQuery({
    queryKey,
    queryFn: () => getReactionSummary(targetType, targetId),
  });

  const toggleMutation = useMutation({
    mutationFn: (kind: ReactionKind) => toggleReaction(targetType, targetId, kind),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const canReact = status === 'authenticated';

  return (
    <div className="flex items-center gap-1.5">
      {REACTION_META.map(({ kind, emoji, label }) => {
        const summary = reactions.find(r => r.kind === kind);
        const count = summary?.count ?? 0;
        const isOwn = summary?.isOwn ?? false;

        return (
          <button
            key={kind}
            disabled={!canReact || toggleMutation.isPending}
            onClick={() => toggleMutation.mutate(kind)}
            aria-label={`${label}${count > 0 ? ` · ${count}` : ''}`}
            aria-pressed={isOwn}
            className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-all disabled:opacity-40 ${
              isOwn
                ? 'border-[#1a56a0] bg-blue-50 font-semibold text-[#1a56a0]'
                : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            <span>{emoji}</span>
            {count > 0 && <span>{count}</span>}
          </button>
        );
      })}
    </div>
  );
}
